import { useEffect, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';

/**
 * `await askConfirm('Delete plan "X"? It cannot be undone.')` — the app's styled
 * confirm popup in place of the browser's window.confirm. The text up to the
 * first "?" is the title; the rest is the body. Needs <ConfirmHost/> mounted once.
 */
type Req = { title: string; body?: string; destructive: boolean; resolve: (ok: boolean) => void };

let push: ((r: Req) => void) | null = null;

export function askConfirm(message: string): Promise<boolean> {
  const i = message.indexOf('?');
  const title = (i >= 0 ? message.slice(0, i + 1) : message).trim();
  const body = i >= 0 ? message.slice(i + 1).trim() : '';
  const destructive = /^(delete|remove|terminate|reverse|deactivate|clear|withdraw)\b/i.test(title);
  return new Promise((resolve) => {
    if (!push) { resolve(window.confirm(message)); return; }
    push({ title, body: body || undefined, destructive, resolve });
  });
}

export function ConfirmHost() {
  const [req, setReq] = useState<Req | null>(null);
  useEffect(() => { push = setReq; return () => { push = null; }; }, []);
  const done = (ok: boolean) => { req?.resolve(ok); setReq(null); };
  const verb = req?.title.split(/\s/)[0] ?? 'Confirm';
  return (
    <ConfirmDialog
      open={!!req}
      title={req?.title ?? ''}
      description={req?.body}
      destructive={req?.destructive}
      confirmLabel={verb.length <= 12 ? verb : 'Confirm'}
      onCancel={() => done(false)}
      onConfirm={() => done(true)}
    />
  );
}
